import * as THREE from 'three';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import type { Viewport } from '../scene/viewport.ts';
import { state } from '../state.ts';

/** Where the offset gizmo/deviation viz for one actor anchors this frame. */
export interface OffsetAnchor {
  actorId: string;
  color: number;
  /** Where the mocap would place the hips (scale applied, offset NOT). */
  from: THREE.Vector3;
  /** The final adjusted hips position. */
  to: THREE.Vector3;
  offsetLength: number;
}

export interface EditGizmosDeps {
  /** Selected contact pin with a point target, or null. */
  getSelectedPinPoint: () => { pinId: string; point: [number, number, number] } | null;
  /** The Adjust panel's current actor (must have a retargeter), or null. */
  getOffsetTarget: () => OffsetAnchor | null;
  /** Every adjusted actor (non-zero offset) — each gets a deviation line. */
  getDeviations: () => OffsetAnchor[];
  onMovePinPoint: (pinId: string, position: THREE.Vector3) => void;
  /** The offset marker was dragged to a world position; main derives the offset. */
  onMoveOffsetMarker: (actorId: string, position: THREE.Vector3) => void;
}

/**
 * Viewport gizmos for the retargeting tools: a draggable teal marker for the
 * selected contact pin's point target, a draggable marker on the Adjust
 * panel's actor (moves its offset directly), and dotted deviation lines from
 * every adjusted avatar's original position to its adjusted one. One shared
 * TransformControls; a selected pin outranks the offset marker. Free view
 * only, never while exporting.
 */
export function createEditGizmos(vp: Viewport, deps: EditGizmosDeps) {
  const group = new THREE.Group();
  group.name = 'EditGizmos';
  group.visible = false;
  vp.scene.add(group);

  const pinMarker = new THREE.Mesh(
    new THREE.SphereGeometry(0.05, 16, 12),
    new THREE.MeshBasicMaterial({ color: 0x53d0d8, depthTest: false, transparent: true, opacity: 0.95 })
  );
  pinMarker.renderOrder = 12;
  pinMarker.visible = false;
  group.add(pinMarker);

  const offsetMarker = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.08),
    new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false, transparent: true, opacity: 0.9 })
  );
  offsetMarker.renderOrder = 12;
  offsetMarker.visible = false;
  group.add(offsetMarker);

  /** actorId → dashed deviation line (original → adjusted). */
  const lines = new Map<string, THREE.Line>();
  const lineGroup = new THREE.Group();
  group.add(lineGroup);

  const tc = new TransformControls(vp.camera, vp.renderer.domElement);
  tc.setMode('translate');
  tc.setSize(0.65);
  const tcHelper = tc.getHelper();
  tcHelper.visible = false;
  vp.scene.add(tcHelper);
  let dragging = false;
  /** What the gizmo is currently driving. */
  let attached: { kind: 'pin'; pinId: string } | { kind: 'offset'; actorId: string } | null = null;

  tc.addEventListener('dragging-changed', (e) => {
    dragging = e.value as boolean;
    if (state.cameraView === 'free') vp.controls.enabled = !dragging;
  });
  tc.addEventListener('objectChange', () => {
    if (!attached || !tc.object) return;
    if (attached.kind === 'pin') deps.onMovePinPoint(attached.pinId, tc.object.position);
    else deps.onMoveOffsetMarker(attached.actorId, tc.object.position);
  });

  function attach(target: typeof attached, marker: THREE.Object3D | null): void {
    attached = target;
    if (marker) {
      if (tc.object !== marker) tc.attach(marker);
      tcHelper.visible = group.visible;
    } else {
      tc.detach();
      tcHelper.visible = false;
    }
  }

  function lineFor(id: string, color: number): THREE.Line {
    let line = lines.get(id);
    if (!line) {
      line = new THREE.Line(
        new THREE.BufferGeometry(),
        new THREE.LineDashedMaterial({
          color,
          dashSize: 0.06,
          gapSize: 0.05,
          transparent: true,
          opacity: 0.65,
          depthTest: false,
        })
      );
      line.renderOrder = 11;
      line.frustumCulled = false;
      lines.set(id, line);
      lineGroup.add(line);
    }
    (line.material as THREE.LineDashedMaterial).color.setHex(color);
    return line;
  }

  /** Per-frame: markers/lines follow the animated hips (unless being dragged). */
  function tick(): void {
    if (!group.visible) return;

    // Deviation lines for every adjusted actor.
    const deviations = deps.getDeviations();
    const seen = new Set<string>();
    for (const d of deviations) {
      seen.add(d.actorId);
      const line = lineFor(d.actorId, d.color);
      line.geometry.setFromPoints([d.from, d.to]);
      line.computeLineDistances();
      line.visible = d.offsetLength > 0.01;
    }
    for (const [id, line] of lines) {
      if (!seen.has(id)) {
        lineGroup.remove(line);
        line.geometry.dispose();
        (line.material as THREE.Material).dispose();
        lines.delete(id);
      }
    }

    // Keep the attached marker glued to its live anchor while NOT dragging.
    if (dragging) return;
    const pin = deps.getSelectedPinPoint();
    if (pin) pinMarker.position.fromArray(pin.point);
    const offsetTarget = deps.getOffsetTarget();
    if (offsetTarget) {
      offsetMarker.position.copy(offsetTarget.to);
      (offsetMarker.material as THREE.MeshBasicMaterial).color.setHex(offsetTarget.color);
    }
  }

  /** Reflect selection/view state (called on every notify()). */
  function update(): void {
    const editMode = state.cameraView === 'free' && !state.exporting;
    group.visible = editMode;
    tc.enabled = editMode;
    if (!editMode) {
      tcHelper.visible = false;
      return;
    }
    if (dragging) return;

    const pin = deps.getSelectedPinPoint();
    const offsetTarget = deps.getOffsetTarget();
    pinMarker.visible = !!pin;
    offsetMarker.visible = !pin && !!offsetTarget;

    if (pin) {
      pinMarker.position.fromArray(pin.point);
      attach({ kind: 'pin', pinId: pin.pinId }, pinMarker);
    } else if (offsetTarget) {
      offsetMarker.position.copy(offsetTarget.to);
      attach({ kind: 'offset', actorId: offsetTarget.actorId }, offsetMarker);
    } else {
      attach(null, null);
    }
  }

  return { update, tick };
}
